/**
 * The changes Praxis suggested after new data, and the attorney's decisions.
 *
 * The progress stream says when a record lands or moves, which reads the view
 * again. A decision writes the record it changes through that record's own
 * service, so everything read off the task is read again with it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptChange,
  amendChange,
  readNotifications,
  rejectChange,
} from "@/services/notifications";
import { TASKS_KEY, notificationsKey } from "./query-keys";

/** Every pending change of the task, in v0.28's cadence. */
export function useNotifications(taskId: string) {
  return useQuery({
    queryKey: notificationsKey(taskId),
    queryFn: () => readNotifications(taskId),
    enabled: taskId !== "",
  });
}

/** Accept, amend and reject, each reading the task again once it settles. */
export function useNotificationWrites(taskId: string) {
  const client = useQueryClient();
  // The decision writes an element, a position or a claim, and the record: every
  // query nested under the task reads again, as a write on the task does. A refusal
  // reads again too: a change whose record moved is proposed again in its place.
  const landed = () =>
    client.invalidateQueries({ queryKey: [...TASKS_KEY, taskId] });
  return {
    accept: useMutation({
      mutationFn: (actionId: string) => acceptChange(taskId, actionId),
      onSettled: landed,
    }),
    amend: useMutation({
      mutationFn: (given: { actionId: string; text: string }) =>
        amendChange(taskId, given.actionId, given.text),
      onSettled: landed,
    }),
    reject: useMutation({
      mutationFn: (actionId: string) => rejectChange(taskId, actionId),
      onSettled: landed,
    }),
  };
}
